import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  GraduationCap,
  Users,
  CreditCard,
  CalendarCheck,
  BarChart3,
  Globe,
  BookOpen,
  Shield,
} from "lucide-react";
import { LoginForm } from "@/features/auth/pages/login.page";
import { useUIStore } from "@/stores/ui.store";
import { cn } from "@/lib/utils";

/* ═══════════════════════════════════════════════════════════
   STYLE 1: CLASSIC — Split-screen with branding left, form right
   Traditional SaaS login with gradient panel + feature showcase
   ═══════════════════════════════════════════════════════════ */

const FEATURE_ICONS = [
  { icon: Users, key: "landing.featureStudents" },
  { icon: CreditCard, key: "landing.featureFees" },
  { icon: CalendarCheck, key: "landing.featureAttendance" },
  { icon: BarChart3, key: "landing.featureReports" },
  { icon: Globe, key: "landing.featureLanguages" },
];

const STATS = [
  { stat: "2,000+", key: "landing.statSchools" },
  { stat: "500K+", key: "landing.statStudents" },
  { stat: "99.9%", key: "landing.statUptime" },
];

function ClassicLogin() {
  const { t } = useTranslation("common");

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_480px] xl:grid-cols-2">
      {/* Left panel — branding showcase */}
      <div className="relative hidden overflow-hidden lg:block">
        <div className="absolute inset-0 bg-linear-to-br from-primary via-primary/95 to-primary/80" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
            backgroundSize: "32px 32px",
          }}
        />
        <div className="animate-float absolute -right-24 -top-24 h-[500px] w-[500px] rounded-full bg-white/4" />
        <div className="animate-float-delayed absolute -bottom-40 -left-40 h-[600px] w-[600px] rounded-full bg-white/3" />
        <div className="animate-float-slow absolute right-1/4 top-1/3 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

        <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14">
          <div className="flex items-center gap-3 text-primary-foreground">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20 shadow-lg shadow-black/10 backdrop-blur-sm">
              <GraduationCap className="h-6 w-6" />
            </div>
            <div>
              <span className="text-2xl font-bold tracking-tight">Skul</span>
              <span className="ml-2 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-sm">
                Beta
              </span>
            </div>
          </div>

          <div className="max-w-lg space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-primary-foreground xl:text-5xl">
                {t("landing.headline")}{" "}
                <span className="text-white/70">{t("landing.headlineAccent")}</span>
              </h1>
              <p className="text-base leading-relaxed text-primary-foreground/70 xl:text-lg">
                {t("landing.description")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {FEATURE_ICONS.map((f) => (
                <div
                  key={f.key}
                  className="flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-2 text-sm font-medium text-primary-foreground/90 backdrop-blur-sm"
                >
                  <f.icon className="h-3.5 w-3.5" />
                  {t(f.key)}
                </div>
              ))}
            </div>
            <div className="flex gap-8">
              {STATS.map((item) => (
                <div key={item.key}>
                  <p className="text-3xl font-bold tracking-tight text-primary-foreground">
                    {item.stat}
                  </p>
                  <p className="text-sm text-primary-foreground/50">{t(item.key)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <blockquote className="max-w-md border-l-2 border-white/20 pl-4">
              <p className="text-sm leading-relaxed text-primary-foreground/70 italic">
                &ldquo;{t("landing.testimonial")}&rdquo;
              </p>
              <footer className="mt-2 text-xs text-primary-foreground/50">
                <strong className="font-medium text-primary-foreground/70">
                  {t("landing.testimonialAuthor")}
                </strong>
                {" "}&mdash; {t("landing.testimonialRole")}
              </footer>
            </blockquote>
            <div className="flex items-center justify-between">
              <p className="text-xs text-primary-foreground/40">
                &copy; {new Date().getFullYear()} {t("landing.copyright")}
              </p>
              <div className="flex gap-3">
                <button className="text-xs text-primary-foreground/40 transition-colors hover:text-primary-foreground/70">
                  {t("landing.privacy")}
                </button>
                <button className="text-xs text-primary-foreground/40 transition-colors hover:text-primary-foreground/70">
                  {t("landing.terms")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex flex-col bg-background">
        <div className="flex items-center gap-2.5 p-6 lg:hidden">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <GraduationCap className="h-5 w-5" />
          </div>
          <span className="text-xl font-bold tracking-tight">Skul</span>
        </div>
        <div className="flex flex-1 items-center justify-center px-6 pb-12 sm:px-10">
          <Outlet />
        </div>
        <div className="px-6 pb-6 text-center lg:hidden">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} {t("landing.copyright")}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLE 2: CENTERED — Hero banner + overlapping card
   Top branded banner with ticker, form card rises up overlapping it.
   Inspired by Apple product pages — layered depth, not flat.
   ═══════════════════════════════════════════════════════════ */

function CenteredLogin() {
  const { t } = useTranslation("common");

  return (
    <div className="relative min-h-screen bg-zinc-100 dark:bg-zinc-900">
      {/* Top hero banner — takes ~45% of viewport */}
      <div className="relative overflow-hidden bg-linear-to-br from-primary via-primary/95 to-primary/80 pb-32 pt-8 lg:pb-40 lg:pt-10">
        {/* Subtle pattern */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 0.5px, transparent 0)`,
            backgroundSize: "20px 20px",
          }}
        />
        {/* Floating shapes */}
        <div className="animate-float absolute -right-20 -top-20 h-80 w-80 rounded-full bg-white/5" />
        <div className="animate-float-delayed absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-white/4" />

        <div className="relative z-10 mx-auto max-w-5xl px-6">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                <GraduationCap className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight text-white">Skul</span>
            </div>
            <div className="flex items-center gap-4">
              <button className="text-xs text-white/50 transition-colors hover:text-white/80">
                {t("landing.privacy")}
              </button>
              <button className="text-xs text-white/50 transition-colors hover:text-white/80">
                {t("landing.terms")}
              </button>
            </div>
          </div>

          {/* Hero content */}
          <div className="mt-8 max-w-xl lg:mt-10">
            <h1 className="text-3xl font-bold leading-[1.1] tracking-tight text-white lg:text-4xl xl:text-5xl">
              {t("landing.headline")}{" "}
              <span className="text-white/60">{t("landing.headlineAccent")}</span>
            </h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60 lg:text-base">
              {t("landing.description")}
            </p>
          </div>

          {/* Stats row */}
          <div className="mt-6 flex gap-8 lg:gap-12">
            {STATS.map((item) => (
              <div key={item.key}>
                <p className="text-2xl font-bold tracking-tight text-white lg:text-3xl">
                  {item.stat}
                </p>
                <p className="text-xs text-white/40">{t(item.key)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Feature ticker strip at banner bottom */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-white/5 backdrop-blur-sm">
          <div className="mx-auto flex max-w-5xl items-center gap-6 overflow-x-auto px-6 py-3 scrollbar-none">
            {FEATURE_ICONS.map((f) => (
              <div
                key={f.key}
                className="flex shrink-0 items-center gap-2 text-xs font-medium text-white/70"
              >
                <f.icon className="h-3.5 w-3.5" />
                {t(f.key)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form card — overlaps the banner by pulling up with negative margin */}
      <div className="relative z-20 mx-auto -mt-24 max-w-[440px] px-4 pb-12 lg:-mt-36">
        <div className="animate-fade-in-up rounded-2xl border border-border bg-background p-8 shadow-xl shadow-black/10 dark:border-white/10 dark:bg-zinc-800/80 dark:shadow-black/30 dark:backdrop-blur-xl">
          <div className="mb-6 text-center">
            <h2 className="text-xl font-bold">{t("auth.signIn")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("auth.signInDescription")}</p>
          </div>
          <LoginForm />
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-muted-foreground/50">
          &copy; {new Date().getFullYear()} {t("landing.copyright")}
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLE 3: MAGAZINE — Editorial asymmetric typography
   Dark bg, giant headline on left, narrow form column on right,
   vertical accent lines, monospaced metadata, cinematic feel
   ═══════════════════════════════════════════════════════════ */

function MagazineLogin() {
  const { t } = useTranslation("common");

  return (
    <div className="relative min-h-screen bg-zinc-50 text-foreground dark:bg-zinc-950 dark:text-white">
      {/* Background color accents */}
      <div className="absolute -left-40 top-1/4 h-[500px] w-[500px] rounded-full bg-primary/6 blur-[120px] dark:bg-primary/8" />
      <div className="absolute -right-32 bottom-1/4 h-[400px] w-[400px] rounded-full bg-violet-500/5 blur-[100px] dark:bg-violet-600/6" />
      <div className="absolute left-1/3 top-0 h-64 w-64 rounded-full bg-emerald-500/4 blur-[80px] dark:bg-emerald-500/5" />

      {/* Subtle noise texture */}
      <div
        className="absolute inset-0 opacity-[0.02] dark:opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Accent vertical line */}
      <div className="absolute left-[55%] top-0 hidden h-full w-px bg-linear-to-b from-transparent via-border to-transparent dark:via-primary/15 lg:block" />

      {/* Grid layout: ~55/45 split — form shifted left */}
      <div className="relative z-10 grid min-h-screen lg:grid-cols-[1fr_460px] xl:grid-cols-[1fr_480px]">
        {/* Left: Editorial content */}
        <div className="flex flex-col justify-between p-8 lg:p-14 xl:p-20">
          {/* Top — logo + meta */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary/15">
                <GraduationCap className="h-5 w-5 text-primary" />
              </div>
              <span className="text-lg font-bold tracking-tight">Skul</span>
            </div>
            <span className="hidden font-mono text-[11px] tracking-wider text-muted-foreground/50 dark:text-white/30 lg:block">
              EST. 2024 &mdash; AFRICA
            </span>
          </div>

          {/* Center — giant typography */}
          <div className="hidden lg:block">
            <div className="space-y-6">
              {/* Oversized split text */}
              <div className="space-y-1">
                <h1 className="text-[clamp(3rem,8vw,7rem)] font-black leading-[0.85] tracking-tighter">
                  {t("landing.headline").replace(",", "")}
                </h1>
                <p className="text-[clamp(1.5rem,3.5vw,3rem)] font-light leading-tight tracking-tight text-primary/40 dark:text-primary/50">
                  {t("landing.headlineAccent")}
                </p>
              </div>

              {/* Horizontal rule with label */}
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-linear-to-r from-primary/15 via-border to-transparent dark:from-primary/20 dark:via-white/10" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground/40 dark:text-white/25">
                  {t("app.tagline")}
                </span>
                <div className="h-px w-12 bg-border dark:bg-white/10" />
              </div>

              {/* Feature grid */}
              <div className="grid grid-cols-3 gap-6 pt-4">
                {[
                  { icon: BookOpen, key: "landing.featureStudents", accent: "text-blue-500/50 dark:text-blue-400/60" },
                  { icon: CreditCard, key: "landing.featureFees", accent: "text-emerald-500/50 dark:text-emerald-400/60" },
                  { icon: BarChart3, key: "landing.featureReports", accent: "text-violet-500/50 dark:text-violet-400/60" },
                ].map((f) => (
                  <div key={f.key} className="space-y-2">
                    <f.icon className={cn("h-5 w-5", f.accent)} />
                    <p className="text-xs font-medium text-muted-foreground/70 dark:text-white/50">{t(f.key)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom — stats */}
          <div className="hidden items-center gap-10 lg:flex">
            {STATS.map((item) => (
              <div key={item.key}>
                <p className="font-mono text-2xl font-bold tracking-tight">{item.stat}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground/50 dark:text-white/30">
                  {t(item.key)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Form column — slightly wider, shifted left */}
        <div className="flex flex-col justify-center bg-background/50 px-8 py-12 backdrop-blur-sm lg:border-l lg:border-border lg:px-14 dark:bg-transparent dark:border-white/5">
          {/* Mobile logo */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <GraduationCap className="h-6 w-6 text-foreground/70" />
            <span className="text-xl font-bold tracking-tight">Skul</span>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-bold">{t("auth.signIn")}</h2>
            <p className="mt-1 text-sm text-muted-foreground dark:text-white/40">{t("auth.signInDescription")}</p>
          </div>
          <div className="dark:hidden">
            <LoginForm />
          </div>
          <div className="hidden dark:block">
            <LoginForm variant="dark" />
          </div>

          {/* Footer */}
          <div className="mt-8 flex items-center gap-3 text-[11px] text-muted-foreground/40 dark:text-white/20">
            <span>&copy; {new Date().getFullYear()} Skul</span>
            <span>&middot;</span>
            <button className="hover:text-foreground/60 dark:hover:text-white/40 transition-colors">{t("landing.privacy")}</button>
            <span>&middot;</span>
            <button className="hover:text-foreground/60 dark:hover:text-white/40 transition-colors">{t("landing.terms")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLE 4: IMMERSIVE — Dark sidebar form + showcase grid
   Dark form panel on left with vertical branding. Right side has
   a large showcase area with staggered perspective feature cards,
   stats, and testimonial. App-store / dashboard-preview vibe.
   ═══════════════════════════════════════════════════════════ */

const SHOWCASE_FEATURES = [
  { icon: Users, key: "landing.featureStudents", color: "from-blue-500/20 to-blue-600/5 border-blue-500/20" },
  { icon: CreditCard, key: "landing.featureFees", color: "from-emerald-500/20 to-emerald-600/5 border-emerald-500/20" },
  { icon: CalendarCheck, key: "landing.featureAttendance", color: "from-amber-500/20 to-amber-600/5 border-amber-500/20" },
  { icon: BarChart3, key: "landing.featureReports", color: "from-violet-500/20 to-violet-600/5 border-violet-500/20" },
  { icon: Globe, key: "landing.featureLanguages", color: "from-rose-500/20 to-rose-600/5 border-rose-500/20" },
  { icon: Shield, key: "auth.sslEncrypted", color: "from-teal-500/20 to-teal-600/5 border-teal-500/20" },
];

function ImmersiveLogin() {
  const { t } = useTranslation("common");

  return (
    <div className="grid min-h-screen bg-[#0a0c10] text-white lg:grid-cols-[420px_1fr] xl:grid-cols-[460px_1fr]">
      {/* ── Left: form panel ── */}
      <div className="relative flex flex-col border-r border-white/[0.06]">
        <div className="absolute inset-0 bg-linear-to-b from-primary/[0.04] to-transparent" />

        <div className="relative z-10 flex flex-1 flex-col justify-between p-8 xl:p-12">
          {/* Branding */}
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 border border-primary/20">
              <GraduationCap className="h-4.5 w-4.5 text-primary" />
            </div>
            <span className="text-base font-bold tracking-tight">Skul</span>
          </div>

          {/* Form */}
          <div>
            <div className="mb-7">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary/70 mb-2">
                Welcome back
              </p>
              <h1 className="text-2xl font-bold tracking-tight">{t("auth.signIn")}</h1>
              <p className="mt-1.5 text-sm text-white/35">{t("auth.signInDescription")}</p>
            </div>
            <LoginForm variant="dark" />
          </div>

          {/* Footer */}
          <div className="flex items-center gap-3 text-[11px] text-white/20">
            <span>&copy; {new Date().getFullYear()} Skul</span>
            <span className="text-white/10">&middot;</span>
            <button className="transition-colors hover:text-white/40">{t("landing.privacy")}</button>
            <span className="text-white/10">&middot;</span>
            <button className="transition-colors hover:text-white/40">{t("landing.terms")}</button>
          </div>
        </div>
      </div>

      {/* ── Right: product showcase ── */}
      <div className="relative hidden overflow-hidden lg:block">
        {/* Ambient glow blobs */}
        <div className="pointer-events-none absolute -right-48 -top-48 h-[600px] w-[600px] rounded-full bg-primary/10 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-48 left-24 h-[500px] w-[500px] rounded-full bg-violet-600/8 blur-[120px]" />

        <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14">

          {/* Top: headline */}
          <div className="max-w-lg">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/20">
              Built for African schools
            </p>
            <h2 className="text-3xl font-bold leading-[1.15] tracking-tight xl:text-4xl">
              {t("landing.headline")}{" "}
              <span className="bg-linear-to-r from-primary to-primary/50 bg-clip-text text-transparent">
                {t("landing.headlineAccent")}
              </span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-white/35 max-w-sm">
              {t("landing.description")}
            </p>
          </div>

          {/* Middle: mock dashboard UI */}
          <div className="my-8 xl:my-0">
            {/* Mock app window */}
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] overflow-hidden shadow-2xl shadow-black/40">
              {/* Titlebar */}
              <div className="flex items-center gap-2 border-b border-white/[0.06] px-4 py-2.5 bg-white/[0.02]">
                <div className="flex gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-white/10" />
                  <div className="h-2.5 w-2.5 rounded-full bg-white/10" />
                  <div className="h-2.5 w-2.5 rounded-full bg-white/10" />
                </div>
                <div className="mx-auto h-4 w-36 rounded-full bg-white/[0.05] border border-white/[0.06]" />
              </div>

              {/* Mock dashboard body */}
              <div className="flex">
                {/* Sidebar strip */}
                <div className="w-10 border-r border-white/[0.05] bg-white/[0.02] flex flex-col items-center gap-2 py-3 xl:w-12">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={cn(
                        "h-7 w-7 rounded-lg",
                        i === 0 ? "bg-primary/20 border border-primary/30" : "bg-white/[0.04]",
                      )}
                    />
                  ))}
                </div>

                {/* Main area */}
                <div className="flex-1 p-3 xl:p-4 space-y-3">
                  {/* Stat cards row */}
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: "Students", val: "1,284", color: "border-blue-500/20 bg-blue-500/[0.06]" },
                      { label: "Present", val: "94%", color: "border-emerald-500/20 bg-emerald-500/[0.06]" },
                      { label: "Fees Due", val: "KES 340K", color: "border-amber-500/20 bg-amber-500/[0.06]" },
                      { label: "Exams", val: "3 Today", color: "border-violet-500/20 bg-violet-500/[0.06]" },
                    ].map((card) => (
                      <div key={card.label} className={cn("rounded-lg border p-2 xl:p-2.5", card.color)}>
                        <p className="text-[8px] xl:text-[9px] text-white/35 mb-0.5">{card.label}</p>
                        <p className="text-[10px] xl:text-xs font-bold text-white/75">{card.val}</p>
                      </div>
                    ))}
                  </div>

                  {/* Table-like rows */}
                  <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/[0.05] bg-white/[0.02]">
                      <div className="h-1.5 w-16 rounded-full bg-white/20" />
                      <div className="ml-auto h-5 w-12 rounded-md bg-primary/20 border border-primary/20" />
                    </div>
                    {[0.55, 0.40, 0.35, 0.30].map((opacity, i) => (
                      <div key={i} className="flex items-center gap-2.5 px-3 py-1.5 border-b border-white/[0.03] last:border-0">
                        <div className="h-5 w-5 rounded-full bg-white/10 shrink-0" />
                        <div className="h-1.5 flex-1 rounded-full" style={{ background: `rgba(255,255,255,${opacity * 0.3})`, width: `${60 + i * 8}%` }} />
                        <div className="h-4 w-8 rounded bg-emerald-500/15 border border-emerald-500/20 shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom: stats */}
          <div className="flex items-center gap-8 xl:gap-12">
            {STATS.map((item) => (
              <div key={item.key}>
                <p className="text-xl font-bold xl:text-2xl">{item.stat}</p>
                <p className="text-[10px] uppercase tracking-wider text-white/25">{t(item.key)}</p>
              </div>
            ))}
            <div className="ml-auto max-w-[200px] border-l border-white/[0.08] pl-6">
              <blockquote className="text-[11px] leading-relaxed text-white/30 italic">
                &ldquo;{t("landing.testimonial").slice(0, 80)}&hellip;&rdquo;
              </blockquote>
              <p className="mt-1.5 text-[10px] text-white/20">{t("landing.testimonialAuthor")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLE 5: MINIMAL — Ultra-clean, oversized typography
   White/light bg, no decorations, massive "Sign in" heading,
   airy spacing, form at center, logo tiny at top-left
   ═══════════════════════════════════════════════════════════ */

function MinimalLogin() {
  const { t } = useTranslation("common");

  return (
    <div className="relative flex min-h-screen flex-col bg-background">
      {/* Top bar: tiny logo left, links right */}
      <header className="flex items-center justify-between px-8 py-6">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-foreground/70" />
          <span className="text-sm font-semibold tracking-tight text-foreground/70">Skul</span>
        </div>
        <div className="flex items-center gap-4">
          <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            {t("landing.privacy")}
          </button>
          <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            {t("landing.terms")}
          </button>
        </div>
      </header>

      {/* Main content: oversized heading + form */}
      <main className="flex flex-1 flex-col items-center justify-center px-6 pb-16">
        {/* Giant sign-in heading */}
        <div className="mb-12 w-full max-w-[400px] text-center">
          <h1 className="text-[clamp(2.5rem,6vw,4rem)] font-black leading-[1] tracking-tight text-foreground">
            {t("auth.signIn")}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {t("auth.signInDescription")}
          </p>
        </div>

        <div className="w-full max-w-[400px]">
          <LoginForm />
        </div>
      </main>

      {/* Bottom footer */}
      <footer className="px-8 pb-6 text-center">
        <p className="text-xs text-muted-foreground/50">
          &copy; {new Date().getFullYear()} {t("landing.copyright")}
        </p>
      </footer>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLE 6: SPOTLIGHT — Centered beam on dark canvas
   Single focused card floating under a soft top spotlight,
   no distractions — just the form and ambient glow.
   ═══════════════════════════════════════════════════════════ */

function SpotlightLogin() {
  const { t } = useTranslation("common");
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-950 px-6">
      {/* Spotlight beam from top */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px]"
        style={{ background: "radial-gradient(ellipse at top, oklch(0.72 0.19 255 / 0.10) 0%, transparent 65%)" }}
      />
      {/* Outer ring decorations */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-[500px] w-[500px] rounded-full border border-white/[0.03]" />
      </div>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-[700px] w-[700px] rounded-full border border-white/[0.02]" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-sm">
        <div className="mb-10 text-center">
          <div className="inline-flex h-13 w-13 items-center justify-center rounded-2xl bg-primary/15 border border-primary/20 mb-5">
            <GraduationCap className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-white/90">{t("auth.signIn")}</h1>
          <p className="mt-2 text-sm text-white/30">{t("auth.signInDescription")}</p>
        </div>
        <div className="rounded-2xl border border-white/8 bg-white/[0.04] p-8 backdrop-blur-sm">
          <LoginForm variant="dark" />
        </div>
        <p className="mt-6 text-center text-[11px] text-white/15">
          © {new Date().getFullYear()} Skul Education
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLE 7: CARD — Frosted glass card on soft gradient canvas
   The form lives in a floating frosted card. Background is a
   blurred gradient wash — feels native, calm, and modern.
   ═══════════════════════════════════════════════════════════ */

function CardLogin() {
  const { t } = useTranslation("common");
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-6">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-linear-to-135deg from-primary/20 via-background to-primary/10" />
      <div className="absolute inset-0 backdrop-blur-3xl" />
      {/* Floating blobs */}
      <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-primary/8 blur-3xl" />

      {/* Floating card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-3xl border border-border/50 bg-card/85 shadow-2xl shadow-primary/10 backdrop-blur-2xl p-10 space-y-7">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 border border-primary/15">
              <GraduationCap className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-bold">Skul</p>
              <p className="text-[10px] text-muted-foreground">School Management Platform</p>
            </div>
          </div>
          <div className="space-y-1">
            <h1 className="text-xl font-bold tracking-tight">{t("auth.signIn")}</h1>
            <p className="text-sm text-muted-foreground">{t("auth.signInDescription")}</p>
          </div>
          <LoginForm />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLE 8: ORBITAL — Rotating orbital rings with central form
   Animated concentric rings rotate at different speeds on a
   deep dark background. The form is the calm center of the orbit.
   ═══════════════════════════════════════════════════════════ */

function OrbitalLogin() {
  const { t } = useTranslation("common");
  const [angle, setAngle] = useState(0);

  useEffect(() => {
    let raf: number;
    let prev = performance.now();
    function tick(now: number) {
      setAngle((a) => (a + (now - prev) * 0.015) % 360);
      prev = now;
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const features = [
    { icon: Users, label: "Student Records" },
    { icon: CreditCard, label: "Mobile Fees" },
    { icon: CalendarCheck, label: "Attendance" },
    { icon: BarChart3, label: "Analytics" },
  ];

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-950 px-6">
      {/* Orbital rings */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div
          className="h-[700px] w-[700px] rounded-full border border-white/[0.03]"
          style={{ transform: `rotate(${angle}deg)` }}
        />
      </div>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div
          className="h-[520px] w-[520px] rounded-full border border-primary/[0.05]"
          style={{ transform: `rotate(${-angle * 1.4}deg)` }}
        />
      </div>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div
          className="h-[350px] w-[350px] rounded-full border border-white/[0.04]"
          style={{ transform: `rotate(${angle * 0.8}deg)` }}
        />
      </div>
      {/* Center glow */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-56 w-56 rounded-full bg-primary/6 blur-3xl" />
      </div>

      {/* Two-column content */}
      <div className="relative z-10 flex w-full max-w-3xl items-center gap-16">
        {/* Left: features (hidden on mobile) */}
        <div className="hidden lg:block flex-1 space-y-5">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 border border-primary/20">
              <GraduationCap className="h-5 w-5 text-primary" />
            </div>
            <span className="text-lg font-bold text-white/80">Skul</span>
          </div>
          <h2 className="text-2xl font-bold text-white/80 leading-snug">
            The future of<br />school management
          </h2>
          <div className="space-y-3 mt-6">
            {features.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 border border-primary/15">
                  <Icon className="h-3.5 w-3.5 text-primary/70" />
                </div>
                <span className="text-sm text-white/40">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: form */}
        <div className="w-full max-w-sm">
          <div className="rounded-2xl border border-white/8 bg-zinc-950/90 backdrop-blur-xl p-8 space-y-6">
            <div>
              <h1 className="text-lg font-bold text-white/90">{t("auth.signIn")}</h1>
              <p className="mt-1 text-xs text-white/30">{t("auth.signInDescription")}</p>
            </div>
            <LoginForm variant="dark" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN EXPORT — Style switcher
   ═══════════════════════════════════════════════════════════ */

export function PublicLayout() {
  const loginScreenStyle = useUIStore((s) => s.loginScreenStyle);

  switch (loginScreenStyle) {
    case "centered":
      return <CenteredLogin />;
    case "magazine":
      return <MagazineLogin />;
    case "immersive":
      return <ImmersiveLogin />;
    case "minimal":
      return <MinimalLogin />;
    case "spotlight":
      return <SpotlightLogin />;
    case "card":
      return <CardLogin />;
    case "orbital":
      return <OrbitalLogin />;
    case "classic":
    default:
      return <ClassicLogin />;
  }
}
